import type { SyntheticEvent } from 'react';
import { useCallback, useState } from 'react';
import type { Produto } from '../../api/produtos.js';
import { buscarProdutos } from '../../api/produtos.js';
import { useConsulta } from '../../api/useConsulta.js';
import { Botao } from '../../componentes/Botao.js';
import { CampoTexto } from '../../componentes/Campo.js';
import { EstadoVazio } from '../../componentes/EstadoVazio.js';
import { IconeBusca } from '../../componentes/Icones.js';
import { Resultado } from '../../componentes/Resultado.js';
import './BuscaNoCadastro.css';

/** Quantas linhas do cadastro a busca traz: é um seletor, não uma listagem. */
const TAMANHO_DA_BUSCA = 8;

type Props = {
  /** Códigos já na lista de impressão. */
  readonly naLista: readonly string[];
  readonly aoEscolher: (codPro: string) => void;
};

type ProdutoDaBusca = { readonly codPro: string; readonly nome: string };

/**
 * O cadastro devolve uma linha por produto + derivação, e a etiqueta é por
 * produto (§14.1: a derivação fica fora do QR). Duas derivações do mesmo
 * `codPro` viram uma linha só.
 */
function porProduto(itens: readonly Produto[]): readonly ProdutoDaBusca[] {
  return itens.reduce<readonly ProdutoDaBusca[]>(
    (unicos, item) =>
      unicos.some((unico) => unico.codPro === item.codPro)
        ? unicos
        : [...unicos, { codPro: item.codPro, nome: item.nome }],
    [],
  );
}

/**
 * Escolher produtos pelo cadastro sincronizado do ERP (`GET /produtos`).
 *
 * A busca só sai quando a pessoa pede — o campo não consulta a cada tecla. O
 * `codPro` vem do cadastro exatamente como o ERP o guarda, zeros à esquerda e
 * caixa incluídos, e é esse texto que vai para o QR.
 */
export function BuscaNoCadastro({ naLista, aoEscolher }: Props) {
  const [texto, definirTexto] = useState('');
  const [termo, definirTermo] = useState('');

  const carregar = useCallback(
    () => buscarProdutos({ busca: termo, tamanho: TAMANHO_DA_BUSCA }),
    [termo],
  );
  const consulta = useConsulta(termo === '' ? null : carregar);

  function buscar(evento: SyntheticEvent<HTMLFormElement>) {
    evento.preventDefault();
    definirTermo(texto.trim());
  }

  return (
    <div className="busca-cadastro">
      <form className="busca-cadastro__formulario" onSubmit={buscar}>
        <CampoTexto
          rotulo="Buscar no cadastro"
          valor={texto}
          aoMudar={definirTexto}
          ajuda="Código ou nome do produto, como está no ERP"
        />
        <Botao type="submit">Buscar</Botao>
      </form>

      {termo !== '' && (
        <Resultado
          consulta={consulta}
          espera="Buscando no cadastro…"
          estaVazio={(dados) => dados.itens.length === 0}
          vazio={
            <EstadoVazio
              tom="neutro"
              icone={<IconeBusca tamanho={22} />}
              titulo="Nenhum produto encontrado"
              detalhe="O cadastro vem do ERP pela sincronização. Um código que ainda não chegou pode ser digitado na lista ao lado."
            />
          }
        >
          {(dados) => (
            <ul className="busca-cadastro__resultados" aria-label="Produtos encontrados">
              {porProduto(dados.itens).map((produto) => (
                <li key={produto.codPro} className="busca-cadastro__item">
                  <span className="busca-cadastro__codigo">{produto.codPro}</span>
                  <span className="busca-cadastro__nome">{produto.nome}</span>
                  {naLista.includes(produto.codPro) ? (
                    <span className="busca-cadastro__na-lista">Na lista</span>
                  ) : (
                    <Botao
                      altura="pequena"
                      aria-label={`Adicionar ${produto.codPro}`}
                      onClick={() => {
                        aoEscolher(produto.codPro);
                      }}
                    >
                      Adicionar
                    </Botao>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Resultado>
      )}
    </div>
  );
}
